/**
 * AdminGate · v2.7.65+E147 (session-persistence audit)
 *
 * One-time 6-digit PIN gate that wraps the entire Admin Console. Replaces the
 * per-tab passcode prompts that used to live under Pricing / Payments / Legal /
 * Accounting / Support. After unlock, every Administration tab opens directly
 * without any second prompt.
 *
 * Implementation:
 *   - Reuses POST /api/admin/accounting/unlock (per-account PIN check via
 *     _verify_admin_pin server-side). Works for any supervisor or admin master.
 *   - v2.7.65+E147 · Unlock state now lives in LOCALSTORAGE + a
 *     BroadcastChannel so opening a second tab / window inherits the gate
 *     immediately and Admins are never forced to re-PIN mid-shift. The
 *     gate is cleared ONLY on explicit logout (see authSync.js which
 *     already broadcasts `rtg-auth-logout`).
 *   - Wraps AdminDashboardPage and replaces every inner per-tab gate, so the
 *     existing PricingTab/PaymentsTab/LegalTab can render their content
 *     directly when this single gate is satisfied.
 */
import { useEffect, useRef, useState } from "react";
import adminApi from "@/lib/adminApi";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Lock, Unlock } from "lucide-react";
import { toast } from "sonner";

// v2.7.65+E147 · localStorage-backed so it survives tab close AND new
// tabs. Auto-cleared on `rtg-auth-logout` broadcast from authSync.
const STORAGE_KEY = "rtg_admin_gate_pin";
const CHANNEL_KEY = "rtg-admin-gate";
const ADMIN_GATE_PIN_EVT = "admin-gate-pin-ready";

let _gateChannel = null;
function _getChannel() {
  if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") return null;
  if (!_gateChannel) {
    try { _gateChannel = new BroadcastChannel(CHANNEL_KEY); }
    catch { _gateChannel = null; }
  }
  return _gateChannel;
}

/**
 * Module-level cache: once a supervisor unlocks the console in ANY tab,
 * downstream panels in EVERY tab can read window.__adminGatePin and re-use
 * the same PIN for any backend call that still wants a PIN (e.g. the
 * Accounting endpoints which still PIN-check server-side for defense-
 * in-depth).
 */
function setGatePin(pin) {
  if (typeof window === "undefined") return;
  window.__adminGatePin = pin;
  if (pin) localStorage.setItem(STORAGE_KEY, pin);
  else localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(ADMIN_GATE_PIN_EVT, { detail: { pin } }));
  // Notify sibling tabs — they should treat the gate as unlocked without
  // re-prompting the supervisor.
  try { _getChannel()?.postMessage({ type: "gate:set", pin: pin || "" }); }
  catch { /* channel unavailable */ }
}

export function getGatePin() {
  if (typeof window === "undefined") return "";
  if (window.__adminGatePin) return window.__adminGatePin;
  const cached = localStorage.getItem(STORAGE_KEY);
  if (cached) window.__adminGatePin = cached;
  return cached || "";
}

// v2.7.65+E147 · Wipe the gate on explicit logout. authSync already
// fires `rtg-auth-logout` on both the current tab AND every sibling
// tab via BroadcastChannel — we listen once at module load.
if (typeof window !== "undefined") {
  window.addEventListener("rtg-auth-logout", () => {
    try { window.__adminGatePin = ""; localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    try { _getChannel()?.postMessage({ type: "gate:clear" }); } catch { /* noop */ }
  });
}

export default function AdminGate({ children }) {
  const [unlocked, setUnlocked] = useState(() => !!getGatePin());
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!unlocked && inputRef.current) inputRef.current.focus();
  }, [unlocked]);

  // v2.7.65+E147 · Cross-tab gate sync. If any sibling tab unlocks OR
  // logs out, mirror it here.
  useEffect(() => {
    const ch = _getChannel();
    if (!ch) return undefined;
    const onMsg = (ev) => {
      const msg = ev?.data;
      if (!msg) return;
      if (msg.type === "gate:set" && msg.pin) {
        window.__adminGatePin = msg.pin;
        localStorage.setItem(STORAGE_KEY, msg.pin);
        setUnlocked(true);
      } else if (msg.type === "gate:clear") {
        window.__adminGatePin = "";
        localStorage.removeItem(STORAGE_KEY);
        setUnlocked(false);
      }
    };
    ch.addEventListener("message", onMsg);
    return () => { try { ch.removeEventListener("message", onMsg); } catch { /* noop */ } };
  }, []);

  const submit = async () => {
    if (!pin.trim()) return;
    setBusy(true);
    try {
      // Reuses the existing per-account PIN endpoint — server-side it calls
      // _verify_admin_pin which accepts any supervisor's PIN OR the admin
      // master's 565968. Wrong PIN returns 403 (not 401 — keeps the user here).
      await adminApi.post("/admin/accounting/unlock", { pin: pin.trim() });
      setGatePin(pin.trim());
      setUnlocked(true);
      toast.success("Admin Console unlocked");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PIN does not match your account");
      setPin("");
    } finally { setBusy(false); }
  };

  if (unlocked) return children;

  return (
    <div className="min-h-screen bg-[#0c0d10] text-zinc-100 flex items-center justify-center px-4" data-testid="admin-gate">
      <Card className="bg-zinc-900 border-amber-400/40 p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <img src="/buller-taxi-crest.png" alt="" className="h-12 w-12 mx-auto mb-3 object-contain"/>
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold mb-1">Admin Console</div>
          <h2 className="text-lg font-bold flex items-center justify-center gap-2"><Lock className="h-4 w-4 text-amber-400"/>6-digit PIN required</h2>
          <p className="text-xs text-zinc-400 mt-1">Enter <b>your supervisor PIN</b> — works for every supervisor and the admin master.</p>
        </div>
        <Label className="text-xs uppercase tracking-wider text-zinc-400">PIN</Label>
        <Input
          ref={inputRef}
          data-testid="admin-gate-pin-input"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          placeholder="• • • • • •"
          className="bg-zinc-950 border-zinc-700 text-zinc-100 font-mono tracking-[0.4em] text-center text-lg h-14 mt-1"
        />
        <Button
          data-testid="admin-gate-unlock"
          onClick={submit}
          disabled={busy || !pin}
          className="w-full mt-4 h-12 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold"
        >
          <Unlock className="h-4 w-4 mr-2"/>{busy ? "Verifying…" : "Unlock Admin Console"}
        </Button>
      </Card>
    </div>
  );
}

export function clearAdminGate() { setGatePin(""); }
